import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getPushCapability } from "../../../services/pushCapability";
import {
  isPushPermissionUiEnabled,
  requestNotificationPermissionFromUserGesture,
} from "../../../services/pushPermission";
import usePushRegistrationController from "./usePushRegistrationController";
import { requestNotificationPreferencesRefresh } from "./notificationPreferencesEvents";

const STATUS_COPY = Object.freeze({
  loading: ["notification_push_permission_loading_title", "notification_push_permission_loading_detail"],
  unsupported: ["notification_push_permission_unsupported_title", "notification_push_permission_unsupported_detail"],
  unconfigured: ["notification_push_permission_unconfigured_title", "notification_push_permission_unconfigured_detail"],
  insecure_context: ["notification_push_permission_insecure_title", "notification_push_permission_insecure_detail"],
  permission_default: ["notification_push_permission_default_title", "notification_push_permission_default_detail"],
  permission_granted_pending_registration: ["notification_push_permission_granted_title", "notification_push_permission_granted_detail"],
  permission_denied: ["notification_push_permission_denied_title", "notification_push_permission_denied_detail"],
  permission_request_dismissed: ["notification_push_permission_dismissed_title", "notification_push_permission_dismissed_detail"],
  permission_request_unavailable: ["notification_push_permission_unavailable_title", "notification_push_permission_unavailable_detail"],
  permission_request_failed: ["notification_push_permission_failed_title", "notification_push_permission_failed_detail"],
  registration_unavailable: ["notification_push_registration_unavailable_title", "notification_push_registration_unavailable_detail"],
  registration_checking: ["notification_push_registration_checking_title", "notification_push_registration_checking_detail"],
  registration_ready: ["notification_push_registration_ready_title", "notification_push_registration_ready_detail"],
  registration_in_progress: ["notification_push_registration_progress_title", "notification_push_registration_progress_detail"],
  registration_active: ["notification_push_registration_active_title", "notification_push_registration_active_detail"],
  registration_active_permission_blocked: ["notification_push_registration_blocked_title", "notification_push_registration_blocked_detail"],
  registration_active_configuration_unavailable: ["notification_push_registration_config_active_title", "notification_push_registration_config_active_detail"],
  registration_lookup_failed: ["notification_push_registration_lookup_failed_title", "notification_push_registration_lookup_failed_detail"],
  registration_failed: ["notification_push_registration_failed_title", "notification_push_registration_failed_detail"],
  registration_timeout: ["notification_push_registration_timeout_title", "notification_push_registration_timeout_detail"],
  deactivation_in_progress: ["notification_push_deactivation_progress_title", "notification_push_deactivation_progress_detail"],
  deactivation_failed: ["notification_push_deactivation_failed_title", "notification_push_deactivation_failed_detail"],
  deactivation_cleanup_failed: ["notification_push_deactivation_cleanup_title", "notification_push_deactivation_cleanup_detail"],
});

export function getPushPermissionUiState(capability) {
  if (!capability) return "loading";
  if (capability.code === "firebase_client_unsupported") return "unsupported";
  if (!capability.secureContext) return "insecure_context";
  if (!capability.configured) return "unconfigured";
  if (!capability.supported) return "unsupported";
  if (capability.permissionState === "granted") {
    return "permission_granted_pending_registration";
  }
  if (capability.permissionState === "denied") return "permission_denied";
  if (capability.permissionState === "default") return "permission_default";
  return "permission_request_unavailable";
}

function isRequestActionAvailable(status) {
  return (
    status === "permission_default" ||
    status === "permission_request_dismissed" ||
    status === "permission_request_failed"
  );
}

function EnabledPushPermissionSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading");
  const [appContext, setAppContext] = useState("browser");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const triggerRef = useRef(null);
  const continueRef = useRef(null);
  const wasDialogOpenRef = useRef(false);
  const mountedRef = useRef(true);
  const registration = usePushRegistrationController();

  const refreshStatus = useCallback(() => {
    const capability = getPushCapability();
    setAppContext(capability.appContext);
    setStatus(getPushPermissionUiState(capability));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refreshStatus();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshStatus();
    };
    window.addEventListener("focus", refreshStatus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("focus", refreshStatus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshStatus]);

  useEffect(() => {
    if (dialogOpen) {
      wasDialogOpenRef.current = true;
      continueRef.current?.focus();
      const handleKeyDown = (event) => {
        if (event.key === "Escape" && !requesting) setDialogOpen(false);
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
    if (wasDialogOpenRef.current) {
      wasDialogOpenRef.current = false;
      triggerRef.current?.focus();
    }
    return undefined;
  }, [dialogOpen, requesting]);

  const openDialog = () => {
    if (isRequestActionAvailable(status)) setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!requesting) setDialogOpen(false);
  };

  const registerAndReconcile = async (options) => {
    const registered = await registration.actions.registerCurrentDevice(options);
    if (!registered) return false;
    await registration.actions.refresh();
    requestNotificationPreferencesRefresh();
    return true;
  };

  const confirmPermissionRequest = async () => {
    const permissionRequest = requestNotificationPermissionFromUserGesture();
    setRequesting(true);
    try {
      const result = await permissionRequest;
      if (!mountedRef.current) return;
      setStatus(result.status);
      if (result.permission === "granted") {
        await registerAndReconcile({
          permissionGranted: true,
        });
      }
    } finally {
      if (mountedRef.current) {
        setRequesting(false);
        setDialogOpen(false);
      }
    }
  };

  let displayStatus = status;
  if (registration.status.startsWith("deactivation_")) {
    displayStatus = registration.status;
  } else if (registration.active) {
    if (["permission_denied", "permission_default"].includes(status)) {
      displayStatus = "registration_active_permission_blocked";
    } else if (
      !registration.vapidConfigured ||
      ["unconfigured", "unsupported", "insecure_context"].includes(status)
    ) {
      displayStatus = "registration_active_configuration_unavailable";
    } else {
      displayStatus = registration.status;
    }
  } else if (
    [
      "registration_checking",
      "registration_lookup_failed",
      "registration_unavailable",
    ].includes(registration.status)
  ) {
    displayStatus = registration.status;
  } else if (status === "permission_granted_pending_registration") {
    displayStatus =
      registration.status === "registration_feature_disabled"
        ? status
        : registration.status;
  }
  const copy =
    STATUS_COPY[displayStatus] || STATUS_COPY.permission_request_unavailable;
  const permissionAction = isRequestActionAvailable(displayStatus);
  const registrationAction = [
    "registration_ready",
    "registration_failed",
    "registration_timeout",
  ].includes(displayStatus);
  const lookupAction = displayStatus === "registration_lookup_failed";
  const deactivationAction = [
    "registration_active",
    "registration_active_permission_blocked",
    "registration_active_configuration_unavailable",
    "deactivation_failed",
    "deactivation_cleanup_failed",
  ].includes(displayStatus);
  const progressAction = [
    "registration_checking",
    "registration_in_progress",
    "deactivation_in_progress",
    "registration_unavailable",
  ].includes(displayStatus);
  const showAction =
    permissionAction ||
    lookupAction ||
    registrationAction ||
    deactivationAction ||
    progressAction ||
    !["permission_granted_pending_registration"].includes(displayStatus);
  const actionAvailable =
    permissionAction || lookupAction || registrationAction || deactivationAction;
  const actionKey = deactivationAction
    ? displayStatus === "deactivation_cleanup_failed"
      ? "notification_push_deactivation_retry_cleanup"
      : "notification_push_deactivation_action"
    : lookupAction
    ? "notification_push_registration_retry_check"
    : registrationAction
    ? displayStatus === "registration_ready"
      ? "notification_push_registration_action"
      : "notification_push_registration_retry"
    : displayStatus === "registration_in_progress"
    ? "notification_push_registration_progress_action"
    : displayStatus === "deactivation_in_progress"
    ? "notification_push_deactivation_progress_action"
    : ["registration_checking", "registration_unavailable"].includes(
        displayStatus
      )
    ? "notification_push_registration_action"
    : "notification_push_permission_action";
  const handlePrimaryAction = async () => {
    if (permissionAction) openDialog();
    else if (lookupAction) await registration.actions.refresh();
    else if (registrationAction) {
      setRequesting(true);
      try {
        await registerAndReconcile();
      } finally {
        if (mountedRef.current) setRequesting(false);
      }
    }
    else if (deactivationAction) registration.actions.deactivateCurrentDevice();
  };
  const descriptionKey =
    appContext === "browser"
      ? "notification_push_permission_description"
      : "notification_push_permission_description_installed";

  return (
    <section
      className="rp-push-permission"
      aria-labelledby="rp-push-permission-title"
    >
      <div className="rp-push-permission__intro">
        <h3 id="rp-push-permission-title">
          {t("notification_push_permission_title")}
        </h3>
        <p>{t(descriptionKey)}</p>
      </div>

      <div
        className={`rp-push-permission__status rp-push-permission__status--${displayStatus}`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="rp-push-permission__status-icon" aria-hidden="true" />
        <div>
          <strong>{t(copy[0])}</strong>
          <p id="rp-push-permission-status-detail">{t(copy[1])}</p>
          {displayStatus === "permission_denied" ? (
            <p className="rp-push-permission__guidance">
              {t("notification_push_permission_denied_guidance")}
            </p>
          ) : null}
        </div>
      </div>

      {showAction ? (
        <button
          ref={triggerRef}
          type="button"
          className={`rp-push-permission__action${
            deactivationAction ? " rp-push-permission__action--secondary" : ""
          }`}
          disabled={!actionAvailable || requesting}
          aria-describedby="rp-push-permission-status-detail"
          onClick={handlePrimaryAction}
        >
          {t(actionKey)}
        </button>
      ) : null}

      {dialogOpen
        ? createPortal(
            <div
              className="rp-push-permission-dialog__backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closeDialog();
              }}
            >
              <div
                className="rp-push-permission-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="rp-push-permission-dialog-title"
                aria-describedby="rp-push-permission-dialog-description"
              >
                <h3 id="rp-push-permission-dialog-title">
                  {t("notification_push_permission_dialog_title")}
                </h3>
                <p id="rp-push-permission-dialog-description">
                  {t("notification_push_permission_dialog_description")}
                </p>
                <ul>
                  <li>{t("notification_push_permission_dialog_privacy")}</li>
                  <li>{t("notification_push_permission_dialog_control")}</li>
                  <li>{t("notification_push_permission_dialog_preferences")}</li>
                </ul>
                <div className="rp-push-permission-dialog__actions">
                  <button
                    type="button"
                    className="rp-push-permission-dialog__secondary"
                    disabled={requesting}
                    onClick={closeDialog}
                  >
                    {t("notification_push_permission_dialog_cancel")}
                  </button>
                  <button
                    ref={continueRef}
                    type="button"
                    className="rp-push-permission-dialog__primary"
                    disabled={requesting}
                    onClick={confirmPermissionRequest}
                  >
                    {t("notification_push_permission_dialog_continue")}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
}

export default function PushPermissionSection() {
  if (!isPushPermissionUiEnabled()) return null;
  return <EnabledPushPermissionSection />;
}
